import { Badge, Banner, Button, Checkbox, Dialog, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { useCategories } from '../../hooks/useCategories';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { WidgetPreview } from './WidgetPreview';

interface Appearance { color: string; title: string; subtitle: string; greeting: string; intro: string; description: string; suggestions: string[]; position: 'left' | 'right'; avatarUrl: string }
interface LeadCapture { mode: 'none' | 'before'; email: boolean; consentText: string; categoryId: string | null; sequenceId: string | null; welcomeSession: string | null; welcomeMessage: string | null }
interface Widget {
  id: string; name: string; publicKey: string; agentId: string | null; allowedDomains: string[]; active: boolean;
  appearance: Appearance; leadCapture: LeadCapture; handoff: { whatsapp: string | null; message: string | null };
  stats?: { conversations: number; leads: number };
}
interface Conversation { id: string; externalId: string; status: string; pageUrl: string | null; messagesCount: number; lastMessageAt: string; contact: { nome: string; telefone: string } | null }
interface Message { id: string; role: 'contact' | 'ai' | 'agent'; content: string; createdAt: string }
type Draft = Omit<Widget, 'id' | 'publicKey' | 'active' | 'stats'> & { id?: string; publicKey?: string; active?: boolean };

const emptyDraft = (agentId: string | null): Draft => ({
  name: 'Site', agentId, allowedDomains: [], handoff: { whatsapp: null, message: null },
  appearance: { color: '#00684A', title: 'Fale com a gente', subtitle: 'Respondemos na hora', greeting: 'Olá! 👋 Como posso ajudar?', intro: '', description: '', suggestions: ['Quais os preços?', 'Qual o horário?'], position: 'right', avatarUrl: '' },
  leadCapture: { mode: 'none', email: false, consentText: 'Aceito receber mensagens pelo WhatsApp.', categoryId: null, sequenceId: null, welcomeSession: null, welcomeMessage: null },
});

const STATUS: Record<string, { label: string; color: 'green' | 'orange' | 'neutral' }> = { AI: { label: 'IA', color: 'green' }, HUMAN: { label: 'Equipe', color: 'orange' }, CLOSED: { label: 'Encerrada', color: 'neutral' } };

const POSITION_OPTIONS = [
  { value: 'right', label: 'Canto direito' },
  { value: 'left', label: 'Canto esquerdo' },
];
const MODE_OPTIONS = [
  { value: 'none', label: 'Só quando fizer sentido na conversa' },
  { value: 'before', label: 'Antes de começar (formulário)' },
];

/** IA › Widget do site: chat com o agente no site do cliente */
export function AiWidgetPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const { categories } = useCategories({ pageSize: 200 });
  const [data, setData] = useState<{ baseUrl: string; widgets: Widget[] } | null>(null);
  const [locked, setLocked] = useState<string | null>(null);
  const [agents, setAgents] = useState<Array<{ id: string; name: string }>>([]);
  const [sequences, setSequences] = useState<Array<{ id: string; name: string }>>([]);
  const [sessions, setSessions] = useState<Array<{ name: string; displayName?: string | null }>>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [domains, setDomains] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Widget | null>(null);
  const [convsOf, setConvsOf] = useState<Widget | null>(null);
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [transcript, setTranscript] = useState<Message[] | null>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/ai/widgets');
    const j = await r.json().catch(() => ({}));
    if (r.status === 403) { setLocked(j.message || 'Recurso não incluído no seu plano'); return; }
    if (r.ok) setData(j);
  }, [fetchApi]);
  useEffect(() => {
    load();
    fetchApi('/api/ai/agents').then((r) => (r.ok ? r.json() : { agents: [] })).then((d) => setAgents(d.agents || [])).catch(() => undefined);
    fetchApi('/api/sequences').then((r) => (r.ok ? r.json() : { sequences: [] })).then((d) => setSequences(d.sequences || [])).catch(() => undefined);
    fetchApi('/api/connections/whatsapp-sessions').then((r) => (r.ok ? r.json() : [])).then(setSessions).catch(() => undefined);
  }, [load, fetchApi]);

  const edit = (d: Draft) => { setDraft(d); setDomains(d.allowedDomains.join(', ')); };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    const body = JSON.stringify({ ...draft, allowedDomains: domains });
    const r = await fetchApi(draft.id ? `/api/ai/widgets/${draft.id}` : '/api/ai/widgets', { method: draft.id ? 'PUT' : 'POST', body });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível salvar'); return; }
    toast.success('Widget salvo');
    edit({ ...j.widget });
    load();
  };

  const openConvs = async (w: Widget) => {
    setConvsOf(w); setTranscript(null);
    const r = await fetchApi(`/api/ai/widgets/${w.id}/conversations`);
    if (r.ok) setConvs((await r.json()).conversations);
  };

  const snippet = (key: string) => `<script src="${data?.baseUrl || 'https://widget.ingrave.com.br'}/w.js" data-widget="${key}" async></script>`;
  const setA = (p: Partial<Appearance>) => draft && setDraft({ ...draft, appearance: { ...draft.appearance, ...p } });
  const setL = (p: Partial<LeadCapture>) => draft && setDraft({ ...draft, leadCapture: { ...draft.leadCapture, ...p } });

  if (locked) return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="default" description={<>{locked}</>} /></div>;

  const agentOptions = agents.map((a) => ({ value: a.id, label: a.name }));
  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.nome }));
  const sequenceOptions = sequences.map((s) => ({ value: s.id, label: s.name }));
  const sessionOptions = sessions.map((s) => ({ value: s.name, label: s.displayName || s.name }));

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4">
          <div style={{ maxWidth: 760 }}>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Widget do site</h2>
            <div className="text-kumo-subtle">
              Um chat no seu site atendido pelo seu <Link to="/ia/agentes">agente de IA</Link>: responde com a base de conhecimento, captura o WhatsApp do visitante
              (com consentimento) e passa para a equipe quando precisa. Instale colando uma linha no site.
            </div>
          </div>
          {canEdit && !draft && <Button variant="primary" icon={ICONS.Plus} onClick={() => edit(emptyDraft(agents[0]?.id ?? null))} data-testid="ai-widget-new">Novo widget</Button>}
        </div>
      </div>

      <div className="page-content space-y-6">
        {data && !data.baseUrl && <Banner variant="alert" description={<>O servidor do widget (Cloudflare) ainda não foi publicado nesta instalação: dá para configurar, mas o script só funciona depois da publicação.</>} />}
        {!draft && agents.length === 0 && <Banner variant="default" description={<>Crie antes um <Link to="/ia/agentes">agente de IA</Link>: é ele quem atende no widget.</>} />}
        {!draft && data && data.widgets.length === 0 && agents.length > 0 && <Banner variant="default" description={<>Nenhum widget ainda. Crie um, escolha o agente e cole o script no seu site.</>} />}

        {!draft && data && data.widgets.length > 0 && (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {data.widgets.map((w) => (
              <section key={w.id} className="brand-card space-y-2" data-testid={`ai-widget-card-${w.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><span style={{ width: 14, height: 14, borderRadius: 7, background: w.appearance.color, display: 'inline-block' }} /><div className="font-semibold">{w.name}</div></div>
                  <Badge variant={w.active ? 'green' : 'neutral'}>{w.active ? 'Ativo' : 'Pausado'}</Badge>
                </div>
                <div className="text-sm !text-[var(--text-muted)]">Últimos 30 dias: {w.stats?.conversations ?? 0} conversas · {w.stats?.leads ?? 0} leads</div>
                <div className="text-xs !text-[var(--text-muted)]">{w.allowedDomains.length ? w.allowedDomains.join(', ') : 'qualquer site'}</div>
                <div className="flex gap-2 flex-wrap pt-1">
                  {canEdit && <Button variant="secondary" icon={ICONS.Edit} onClick={() => edit({ ...w })}>Editar</Button>}
                  <Button variant="secondary" icon={ICONS.SMS} onClick={() => openConvs(w)} data-testid={`ai-widget-conversations-${w.id}`}>Conversas</Button>
                  <Button variant="secondary" icon={ICONS.Copy} onClick={() => { navigator.clipboard?.writeText(snippet(w.publicKey)); toast.success('Script copiado'); }}>Copiar script</Button>
                  {canEdit && <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => setDeleting(w)}>Excluir</DangerButton>}
                </div>
              </section>
            ))}
          </div>
        )}

        {draft && (
          <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(300px, 2fr)' }}>
            <div className="space-y-6">
              <section className="brand-card space-y-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Field label="Nome">
                      <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} />
                    </Field>
                  </div>
                  {draft.id && <label className="flex items-center gap-2 pb-2"><Switch onCheckedChange={(checked: boolean) => setDraft({ ...draft, active: checked })} aria-label="Ativo" checked={draft.active !== false} /><div>Ativo</div></label>}
                </div>
                {/* O wrapper mantém o data-testid usado pelo e2e; o `Select` do
                    Cloudscape renderiza um <button> como elemento acionável. */}
                <div data-testid="ai-widget-agent">
                  <Field label="Agente que atende">
                    <Select value={(agentOptions.find((o) => o.value === draft.agentId) ?? null)?.value ?? null} items={agentOptions} onValueChange={(value: string | null) => setDraft({ ...draft, agentId: value || null })} placeholder="Escolha o agente" />
                  </Field>
                </div>
                <Field label="Sites permitidos" description="Domínios separados por vírgula (subdomínios valem). Vazio = qualquer site.">
                  <Input onChange={(e) => setDomains(e.target.value)} placeholder="minhaloja.com.br" value={domains} />
                </Field>
              </section>

              <section className="brand-card space-y-4">
                <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Aparência</h3>
                <div className="grid gap-3 grid-cols-2">
                  <Field label="Título">
                    <Input onChange={(e) => setA({ title: e.target.value })} value={draft.appearance.title} />
                  </Field>
                  <Field label="Subtítulo">
                    <Input onChange={(e) => setA({ subtitle: e.target.value })} value={draft.appearance.subtitle} />
                  </Field>
                </div>
                <Field label="Foto do agente" description="URL https de uma imagem quadrada. Sem foto, aparece a inicial do título.">
                  <Input onChange={(e) => setA({ avatarUrl: e.target.value })} value={draft.appearance.avatarUrl} placeholder="https://…" />
                </Field>
                <Field label="Título da boas-vindas" description="Aparece em destaque antes da primeira mensagem.">
                  <Input onChange={(e) => setA({ greeting: e.target.value })} value={draft.appearance.greeting} />
                </Field>
                <Field label="Apresentação" description="Frase curta no balão acima do título, por exemplo: Oi, eu sou a Íris.">
                  <Input onChange={(e) => setA({ intro: e.target.value })} value={draft.appearance.intro} />
                </Field>
                <Field label="Texto de apoio">
                  <Input onChange={(e) => setA({ description: e.target.value })} value={draft.appearance.description} />
                </Field>
                <Field label="Sugestões (até 4, separadas por vírgula)">
                  <Input onChange={(e) => setA({ suggestions: e.target.value.split(',').map((s) => s.trimStart()).slice(0, 4) })} value={draft.appearance.suggestions.join(', ')} />
                </Field>
                <div className="grid gap-3 grid-cols-2">
                  <label className="block"><div className="font-semibold" style={{ fontSize: 13, marginBottom: 4 }}>Cor</div>
                    <input type="color" aria-label="Cor" value={draft.appearance.color} onChange={(e) => setA({ color: e.target.value })} style={{ width: '100%', height: 36, border: '1px solid var(--text-faint)', borderRadius: 6, padding: 2 }} /></label>
                  <Field label="Posição">
                    <Select value={(POSITION_OPTIONS.find((o) => o.value === draft.appearance.position) ?? null)?.value ?? null} items={POSITION_OPTIONS} onValueChange={(value: string | null) => {
                        const v = value;
                        if (v) setA({ position: v as Appearance['position'] });
                      }} />
                  </Field>
                </div>
              </section>

              <section className="brand-card space-y-4">
                <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Captura de contato</h3>
                <Field label="Pedir nome e WhatsApp" description="Mesmo sem formulário, o agente pode pedir os dados durante a conversa">
                  <Select value={(MODE_OPTIONS.find((o) => o.value === draft.leadCapture.mode) ?? null)?.value ?? null} items={MODE_OPTIONS} onValueChange={(value: string | null) => {
                      const v = value;
                      if (v) setL({ mode: v as LeadCapture['mode'] });
                    }} />
                </Field>
                {draft.leadCapture.mode === 'before' && <Checkbox onCheckedChange={(checked: boolean) => setL({ email: checked })} checked={draft.leadCapture.email} label={<>Pedir também o e-mail</>} />}
                <Field label="Texto do consentimento (LGPD)">
                  <Input onChange={(e) => setL({ consentText: e.target.value })} value={draft.leadCapture.consentText} />
                </Field>
                <div className="grid gap-3 grid-cols-2">
                  <Field label="Categoria do contato">
                    <Select value={(categoryOptions.find((o) => o.value === draft.leadCapture.categoryId) ?? null)?.value ?? null} items={categoryOptions} onValueChange={(value: string | null) => setL({ categoryId: value || null })} />
                  </Field>
                  <Field label="Sequência (com consentimento)">
                    <Select value={(sequenceOptions.find((o) => o.value === draft.leadCapture.sequenceId) ?? null)?.value ?? null} items={sequenceOptions} onValueChange={(value: string | null) => setL({ sequenceId: value || null })} />
                  </Field>
                </div>
                <Field label="Conexão que manda a boas-vindas no WhatsApp">
                  <Select value={(sessionOptions.find((o) => o.value === draft.leadCapture.welcomeSession) ?? null)?.value ?? null} items={sessionOptions} onValueChange={(value: string | null) => setL({ welcomeSession: value || null })} />
                </Field>
                {draft.leadCapture.welcomeSession && <InputArea label="Boas-vindas" onChange={(e) => setL({ welcomeMessage: e.target.value })} rows={2} value={draft.leadCapture.welcomeMessage ?? ''} placeholder="Oi {{primeiro_nome}}! Vi que você falou com a gente pelo site. 😊" />}
              </section>

              <section className="brand-card space-y-4">
                <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Passar para a equipe</h3>
                <Field label="WhatsApp da equipe (opcional)" description="Aparece o botão Continuar no WhatsApp quando a IA transfere">
                  <Input onChange={(e) => setDraft({ ...draft, handoff: { ...draft.handoff, whatsapp: e.target.value } })} placeholder="55 63 99999-0000" value={draft.handoff.whatsapp ?? ''} />
                </Field>
                <Field label="Mensagem ao transferir">
                  <Input onChange={(e) => setDraft({ ...draft, handoff: { ...draft.handoff, message: e.target.value } })} placeholder="Vou te passar para a nossa equipe." value={draft.handoff.message ?? ''} />
                </Field>
              </section>

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setDraft(null)} data-testid="ai-widget-close">Fechar</Button>
                <Button variant="primary" loading={busy} disabled={!draft.name.trim() || !draft.agentId} onClick={save} data-testid="ai-widget-save">Salvar</Button>
              </div>
            </div>

            <div className="space-y-4" style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
              <WidgetPreview a={draft.appearance} />
              {draft.publicKey && (
                <section className="brand-card space-y-2">
                  <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Instalar no site</h3>
                  <div className="text-sm !text-[var(--text-muted)]">Cole antes do fechamento do &lt;/body&gt; em todas as páginas.</div>
                  <pre data-testid="ai-widget-snippet" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 8, fontSize: 12 }}>{snippet(draft.publicKey)}</pre>
                  <Button variant="secondary" icon={ICONS.Copy} onClick={() => { navigator.clipboard?.writeText(snippet(draft.publicKey!)); toast.success('Script copiado'); }}>Copiar</Button>
                </section>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog.Root open={!!convsOf} onOpenChange={(o: boolean) => { if (!o) (() => setConvsOf(null))(); }}>
<Dialog size="lg" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Conversas: {convsOf?.name}</h3>}</Dialog.Title>

        {!transcript ? (
          <div className="mt-4 space-y-2" style={{ maxHeight: 520, overflowY: 'auto' }}>
            {convs.length === 0 && <div className="!text-[var(--text-muted)]">Nenhuma conversa ainda.</div>}
            {convs.map((c) => (
              <button key={c.id} type="button" className="brand-card w-full text-left" data-testid={`ai-widget-conversation-${c.id}`} onClick={async () => {
                const r = await fetchApi(`/api/ai/widgets/conversations/${c.id}/messages`);
                if (r.ok) setTranscript((await r.json()).messages);
              }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{c.contact ? `${c.contact.nome} · +${c.contact.telefone}` : `Visitante ${c.externalId.slice(0, 8)}`}</div>
                  <Badge variant={STATUS[c.status]?.color ?? 'neutral'}>{STATUS[c.status]?.label ?? c.status}</Badge>
                </div>
                <div className="text-xs !text-[var(--text-muted)]">{new Date(c.lastMessageAt).toLocaleString('pt-BR')} · {c.messagesCount} mensagens{c.pageUrl ? ` · ${c.pageUrl}` : ''}</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 space-y-2" style={{ maxHeight: 520, overflowY: 'auto' }}>
            <Button variant="secondary" icon={ICONS.ArrowLeft} onClick={() => setTranscript(null)}>Voltar</Button>
            {transcript.map((m) => (
              <div key={m.id} className={m.role === 'contact' ? 'text-right' : ''}>
                <div style={{ display: 'inline-block', maxWidth: '80%', padding: '6px 10px', borderRadius: 10, textAlign: 'left', whiteSpace: 'pre-wrap', background: m.role === 'contact' ? 'var(--tint-green)' : 'var(--surface-2)', border: '1px solid var(--border)' }}>{m.content}</div>
              </div>
            ))}
          </div>
        )}
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={!!deleting} title="Excluir widget" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) { const r = await fetchApi(`/api/ai/widgets/${deleting.id}`, { method: 'DELETE' }); if (r.ok) { toast.success('Widget excluído'); load(); } } setDeleting(null); }}>
        O chat some dos sites onde o script está instalado e as conversas são apagadas. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}
